// Hub ▸ Marketing ▸ Ask (slice S4, CEO 2026-10-22) — plain-English questions.
//
// Claude reads the question as an Explore view chosen ONLY from the metrics
// catalogue (never SQL, never data), the Explore engine answers it, and the
// screen shows: the answer, the table / chart, exactly how the question was
// read ("I read this as: …") with what was assumed ("You didn't give dates —
// I used the last 30 days"), "Open in Explore" and "Save as a view", and —
// when the catalogue can't answer — why, plus views that can. Every sentence
// here comes from our code, not from the model.
import { useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { api, apiErrorMessage } from "@/lib/api";
import { canEditMarketing } from "@/lib/access";
import { EmptyState, ErrorLine, Panel, Skeleton, useApi } from "./kit";
import { ExploreLink, ExploreResultView, SaveForm } from "./ExploreTab";
import type { AskResponse, AskStatus, AskSuggestion } from "./types";

const EXAMPLES = [
  "What did each channel return per ad dollar in the last 30 days?",
  "How much did we spend on LinkedIn last quarter, vs the quarter before?",
  "New clients by how they found us this year",
  "Cost to win a client from paid ads, by month this year",
  "Which campaigns brought the most website leads last month?",
];

function Suggestions({ items, title }: { items: AskSuggestion[]; title: string }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="text-label font-semibold text-ink">{title}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {items.map((s) => (
          <li key={s.readAs}>
            <ExploreLink spec={s.spec} className="inline-flex items-start gap-2 rounded-md border border-rule bg-white px-3 py-2 text-left text-label text-ink hover:bg-paper">
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /><span>{s.readAs[0]!.toUpperCase() + s.readAs.slice(1)}</span>
            </ExploreLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A question box that grows with the text; Enter asks, Shift+Enter starts a new line. */
function QuestionBox({ value, onChange, onSubmit, disabled }: { value: string; onChange: (v: string) => void; onSubmit: () => void; disabled: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(220, el.scrollHeight)}px`; }, [value]);
  return (
    <textarea id="ask-q" ref={ref} rows={1} value={value} maxLength={500} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
      placeholder="e.g. What did LinkedIn cost per new client last quarter?"
      className="min-h-[48px] min-w-0 flex-1 resize-none rounded-md border border-rule bg-white px-4 py-3 text-body leading-snug text-ink placeholder:text-ink-muted" />
  );
}

export function AskTab() {
  const { user } = useAuth();
  const canSave = canEditMarketing(user?.roles);
  const status = useApi<AskStatus>("/api/marketing/ask/status");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<AskResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  async function ask(text: string) {
    const question = text.trim();
    if (question.length < 3 || busy) return;
    setBusy(true); setErr(null); setAnswer(null); setSaving(false); setSaved(null);
    try { setAnswer(await api<AskResponse>("/api/marketing/ask", { method: "POST", body: JSON.stringify({ question }) })); }
    catch (e) { setErr(apiErrorMessage(e, "Ask couldn't answer just now — try again, or use Explore.")); }
    finally { setBusy(false); status.reload(); }
  }
  const s = status.data;
  const off = s && s.state !== "ready" && s.state !== "rate_limited" && s.state !== "busy";
  return (
    <div className="space-y-6">
      <Panel title={<span className="inline-flex items-center gap-2"><Sparkles className="h-5 w-5" aria-hidden />Ask</span>}
        caption="Ask a question in plain English. Claude picks the matching number from the metrics catalogue — it never sees your data or writes its own queries — and Explore works it out.">
        {status.error && <ErrorLine onRetry={status.reload}>{status.error}</ErrorLine>}
        {!s && !status.error && <Skeleton rows={0} label="Checking Ask" />}
        {off && <EmptyState title="Almost ready">{s!.message}</EmptyState>}
        {s && !off && (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void ask(q); }}>
            <label htmlFor="ask-q" className="sr-only">Your question</label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <QuestionBox value={q} onChange={setQ} onSubmit={() => void ask(q)} disabled={busy} />
              <button type="submit" disabled={busy || q.trim().length < 3 || s.state === "rate_limited"} className="h-12 shrink-0 rounded-md bg-ink px-6 text-label font-semibold text-white disabled:opacity-40">{busy ? "Working it out…" : "Ask"}</button>
            </div>
            {s.state === "rate_limited" ? <p className="text-label text-warn">{s.message}</p>
              : <p className="text-meta text-ink-muted">Up to {s.perHour} questions an hour, one at a time{typeof s.usedThisHour === "number" ? ` · ${s.usedThisHour} asked in the last hour` : ""}. Questions are kept 90 days to improve Ask; answers aren't kept.</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {EXAMPLES.map((x) => <button key={x} type="button" onClick={() => { setQ(x); void ask(x); }} disabled={busy} className="rounded-full border border-rule bg-paper px-3 py-1.5 text-left text-meta text-ink hover:bg-white disabled:opacity-40">{x}</button>)}
            </div>
            <details className="pt-1 text-label text-ink">
              <summary className="cursor-pointer font-semibold">What Ask can answer</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-muted">
                {s.canAnswer.map((x) => <li key={x}>{x}</li>)}
                <li>Not yet: website visits and page views (needs the Google Analytics feed), email opens, social reach, forecasts, one named client, supplier costs.</li>
              </ul>
            </details>
          </form>
        )}
      </Panel>

      {err && <ErrorLine>{err}</ErrorLine>}
      {busy && <Skeleton rows={1} label="Working it out" />}
      {answer && (
        <Panel>
          {"question" in answer && <p className="text-meta uppercase tracking-[0.08em] text-ink-muted">You asked: <span className="normal-case tracking-normal text-ink">“{answer.question}”</span></p>}
          {answer.state === "answered" ? (
            <div className="mt-4 space-y-5">
              <div className="space-y-2 rounded-lg bg-paper px-4 py-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-label text-ink"><span className="font-semibold">I read this as:</span> {answer.result.readAs[0]!.toUpperCase() + answer.result.readAs.slice(1)}.</p>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <ExploreLink spec={answer.spec} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-ink px-4 text-label font-semibold text-ink hover:bg-white">Open in Explore<ArrowRight className="h-4 w-4" aria-hidden /></ExploreLink>
                    {canSave && !saving && !saved && <button type="button" onClick={() => setSaving(true)} className="h-10 rounded-md bg-ink px-4 text-label font-semibold text-white">Save as a view</button>}
                  </div>
                </div>
                {answer.assumptions.length > 0 && <ul className="space-y-0.5 text-label text-ink-muted">{answer.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>}
              </div>
              {saving && <SaveForm spec={answer.spec} readAs={answer.result.readAs} onCancel={() => setSaving(false)} onSaved={(m) => { setSaving(false); setSaved(m); }} />}
              {saved && <p role="status" className="inline-flex items-center gap-1.5 rounded-md bg-ok-bg px-3 py-2 text-label font-semibold text-ok"><Check className="h-4 w-4" aria-hidden />{saved}</p>}
              <ExploreResultView result={answer.result} />
              <Suggestions items={answer.suggestions} title="You might also look at" />
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <p className="text-[20px] font-semibold leading-snug text-ink">{answer.message}</p>
              {"suggestions" in answer && <Suggestions items={answer.suggestions} title="These I can answer" />}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
